import TitleBar from '../../../components/TitleBar';
import { Footer } from '../../../components/common/Footer';
import { ColumnsType } from 'antd/es/table';
import { Button } from '../../../components';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { FETCH_FINANCIALS } from '../queries';
import TableV3 from '../../../components/generic/Table/TableV3';
import { tableSortingMethods } from '../../../utils';
import FinancialData from '../../../components/common/FinancialData';
import QueryErrorBoundary from '../../../components/error-handling';
import { baseColumns } from './exportColumns';
import { useUserContext } from '../../../context/PermissionsContext';
import { getFilteredTags } from '../../../utils/getFilteredTags';
import TitleUpdater from '../../../components/common/TitleUpdater';

interface TableData {
  key: string;
  name: string;
  revenue: number;
  costOfSales: number;
  grossMargin: number;
  path?: string;
  children?: TableData[];
}

type ProductFinancials = {
  costOfSales: number;
  grossMargin: number;
  name: string;
  percentage: number;
  revenue: number;
  uuid: string;
};

type ProductGroupFinancials = {
  costOfSales: number;
  grossMargin: number;
  name: string;
  percentage: number;
  revenue: number;
  uuid: string;
  productFinancials: ProductFinancials[];
};

type DivisionFinancial = {
  costOfSales: number;
  grossMargin: number;
  name: string;
  percentage: number;
  productGroupFinancials: ProductGroupFinancials[];
  revenue: number;
  uuid: string;
};

type FindCorporateByUuid = {
  costOfSales: number;
  divisionFinancial: DivisionFinancial[];
  grossMargin: number;
  name: string;
  revenue: number;
  uuid: string;
};

interface DataReturned {
  findCorporateByUuid: FindCorporateByUuid[];
}

const transformData = (data: DataReturned, isCorporate: boolean): any[] => {
  let productData: TableData[] = [];
  let productGroupData: TableData[] = [];
  let divisionData: TableData[] = [];
  let corporateData: TableData[] = [];

  if (data?.findCorporateByUuid)
    for (let i = 0; i < data?.findCorporateByUuid?.length; i++) {
      divisionData = [];
      for (
        let j = 0;
        j < data?.findCorporateByUuid[i].divisionFinancial?.length;
        j++
      ) {
        productGroupData = [];
        for (
          let k = 0;
          k <
          data?.findCorporateByUuid[i].divisionFinancial[j]
            .productGroupFinancials?.length;
          k++
        ) {
          productData = [];
          for (
            let l = 0;
            l <
            data?.findCorporateByUuid[i].divisionFinancial[j]
              .productGroupFinancials[k].productFinancials?.length;
            l++
          ) {
            productData.push({
              key: data.findCorporateByUuid[i].divisionFinancial[j]
                .productGroupFinancials[k].productFinancials[l].uuid,
              name: data.findCorporateByUuid[i].divisionFinancial[j]
                .productGroupFinancials[k].productFinancials[l].name,
              revenue:
                data.findCorporateByUuid[i].divisionFinancial[j]
                  .productGroupFinancials[k].productFinancials[l].revenue,
              costOfSales:
                data.findCorporateByUuid[i].divisionFinancial[j]
                  .productGroupFinancials[k].productFinancials[l].costOfSales,
              grossMargin:
                data.findCorporateByUuid[i].divisionFinancial[j]
                  .productGroupFinancials[k].productFinancials[l].grossMargin,
              path: `/${data.findCorporateByUuid[i].name}/${data.findCorporateByUuid[i].uuid}/${data.findCorporateByUuid[i].divisionFinancial[j].name}/${data.findCorporateByUuid[i].divisionFinancial[j].uuid}/${data.findCorporateByUuid[i].divisionFinancial[j].productGroupFinancials[k].name}/${data.findCorporateByUuid[i].divisionFinancial[j].productGroupFinancials[k].uuid}`,
            });
          }
          productGroupData.push({
            key: data.findCorporateByUuid[i].divisionFinancial[j]
              .productGroupFinancials[k].uuid,
            name: data.findCorporateByUuid[i].divisionFinancial[j]
              .productGroupFinancials[k].name,
            revenue:
              data.findCorporateByUuid[i].divisionFinancial[j]
                .productGroupFinancials[k].revenue,
            costOfSales:
              data.findCorporateByUuid[i].divisionFinancial[j]
                .productGroupFinancials[k].costOfSales,
            grossMargin:
              data.findCorporateByUuid[i].divisionFinancial[j]
                .productGroupFinancials[k].grossMargin,
            children: productData,
            path: `/${data.findCorporateByUuid[i].name}/${data.findCorporateByUuid[i].uuid}/${data.findCorporateByUuid[i].divisionFinancial[j].name}/${data.findCorporateByUuid[i].divisionFinancial[j].uuid}`,
          });
        }

        divisionData.push({
          key: data.findCorporateByUuid[i].divisionFinancial[j].uuid,
          name: data.findCorporateByUuid[i].divisionFinancial[j].name,
          revenue: data.findCorporateByUuid[i].divisionFinancial[j].revenue,
          costOfSales:
            data.findCorporateByUuid[i].divisionFinancial[j].costOfSales,
          grossMargin:
            data.findCorporateByUuid[i].divisionFinancial[j].grossMargin,
          children: productGroupData,
          path: `/${data.findCorporateByUuid[i].name}/${data.findCorporateByUuid[i].uuid}`,
        });
      }

      corporateData.push({
        key: data.findCorporateByUuid[i].uuid,
        name: data.findCorporateByUuid[i].name,
        revenue: data.findCorporateByUuid[i].revenue,
        costOfSales: data.findCorporateByUuid[i].costOfSales,
        grossMargin: data.findCorporateByUuid[i].grossMargin,
        children: divisionData,
        path: `/financials-corporate`,
      });
    }

  return isCorporate ? corporateData : divisionData;
};

function AllFinancials() {
  const params = useParams();
  const navigate = useNavigate();

  const { canViewFinancials, selectedTags, isDivision, isCorporate } =
    useUserContext();

  const { data, loading, error } = useQuery<DataReturned>(FETCH_FINANCIALS, {
    variables: {
      uuid: params.corporateId,
    },
  });

  const filteredDivisions = getFilteredTags({
    data: data?.findCorporateByUuid[0].divisionFinancial || [],
    selectedTags,
    condition: isDivision,
    lookUpKey: 'uuid',
  });

  const groupedData: FindCorporateByUuid = {
    ...data?.findCorporateByUuid[0],
    divisionFinancial: filteredDivisions,
    costOfSales: data?.findCorporateByUuid[0]?.costOfSales ?? 0,
    grossMargin: data?.findCorporateByUuid[0]?.grossMargin ?? 0,
    name: data?.findCorporateByUuid[0]?.name ?? '',
    revenue: data?.findCorporateByUuid[0]?.revenue ?? 0,
    uuid: data?.findCorporateByUuid[0]?.uuid ?? '',
  };

  const columns: ColumnsType<any> = [
    {
      title: '',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: 'Revenue',
      dataIndex: 'revenue',
      key: 'revenue',
      align: 'right',
      width: 100,
      sorter: canViewFinancials
        ? (a, b) => tableSortingMethods.number(a.revenue, b.revenue)
        : undefined,
      render: (text: number) => (
        <span className="custom-table--column">
          {<FinancialData text={text} />}
        </span>
      ),
    },
    {
      title: 'Cost of Sales',
      dataIndex: 'costOfSales',
      key: 'costOfSales',
      align: 'right',
      width: 100,
      sorter: canViewFinancials
        ? (a, b) => tableSortingMethods.number(a.costOfSales, b.costOfSales)
        : undefined,
      render: (text: number) => (
        <span className="custom-table--column">
          {<FinancialData text={text} />}
        </span>
      ),
    },
    {
      title: 'Gross Margin',
      dataIndex: 'grossMargin',
      key: 'grossMargin',
      align: 'right',
      width: 100,
      sorter: canViewFinancials
        ? (a, b) => tableSortingMethods.number(a.grossMargin, b.grossMargin)
        : undefined,
      render: (text: number) => (
        <span className="custom-table--column">
          {<FinancialData text={text} />}
        </span>
      ),
    },
  ];

  const extendedActions = (record: any) => (
    <Button
      type="primary-rounded"
      className="text-sm"
      onClick={() => navigate(record?.path)}
    >
      Review/Edit
    </Button>
  );

  return (
    <>
      <TitleUpdater title="All Financials" />
      <TitleBar title="Data Inputs" />
      <QueryErrorBoundary error={error}>
        <TableV3
          title="All Financials"
          columns={columns}
          data={
            data
              ? transformData(
                  { findCorporateByUuid: [groupedData] },
                  isCorporate,
                )
              : []
          }
          loading={loading}
          extendActions={extendedActions}
          exportExcelColumns={baseColumns}
        />
      </QueryErrorBoundary>
      <Footer backPath={'/financials-corporate'} />
    </>
  );
}

export default AllFinancials;
